import { useState, useEffect, useCallback, useRef } from 'react';
import toast from 'react-hot-toast';
import { AnimatePresence } from 'framer-motion';
import SafeIcon from '../../../../../common/SafeIcon';
import PageHeader from '../../common/PageHeader';
import SettingCard from './components/SettingCard';
import ToggleRow from './components/ToggleRow';
import SelectRow from './components/SelectRow';
import ActionRow from './components/ActionRow';
import SettingsSkeleton from './components/SettingsSkeleton';
import TotpSetupModal from './components/TotpSetupModal';
import PurgeLogsModal from './components/PurgeLogsModal';
import { publicApiRequest } from '../../../../../services/publicApi';
import { adminApiRequest } from '../../../../../services/adminApi';
import { getCookie } from '../../../../../utils/cookies';
import { useAdminAuth } from '../../../../../context/AdminAuthContext';

const TIMEOUT_OPTIONS = [
  { value: '15',  label: '15 minutes' },
  { value: '30',  label: '30 minutes' },
  { value: '60',  label: '1 hour' },
  { value: '120', label: '2 hours' },
];

const TIMEZONE_OPTIONS = [
  { value: 'utc0', label: 'UTC+0 (London)' },
  { value: 'utc3', label: 'UTC+3 (Amman)' },
  { value: 'utc5', label: 'UTC+5 (Karachi)' },
  { value: 'utc8', label: 'UTC+8 (Singapore)' },
];

const CURRENCY_OPTIONS = [
  { value: 'usd', label: 'USD ($)' },
  { value: 'eur', label: 'EUR (€)' },
  { value: 'gbp', label: 'GBP (£)' },
];

const Settings = () => {
  const heroFileInputRef = useRef(null);
  const { admin, updateAdmin } = useAdminAuth();
  const isSuperAdmin = admin?.role === 'superadmin';
  const [regionOptions,   setRegionOptions]   = useState([]);
  const [loading,         setLoading]         = useState(true);
  const [saving,          setSaving]          = useState(false);
  const [exporting,       setExporting]       = useState(false);
  const [exportFormat,    setExportFormat]    = useState('csv');
  const [showTotpModal,   setShowTotpModal]   = useState(false);
  const [disablingTotp,   setDisablingTotp]   = useState(false);
  const [showPurgeModal,  setShowPurgeModal]  = useState(false);
  const [purging,         setPurging]         = useState(false);

  const [security, setSecurity] = useState({
    require2fa:      false,
    strongPasswords: false,
    sessionTimeout:  '30',
  });

  const [general, setGeneral] = useState({
    region:   '',
    timezone: 'utc3',
    currency: 'usd',
  });

  const [notifications, setNotifications] = useState({
    emailAlerts:         true,
    weeklyDigest:        false,
    failedLoginWarnings: true,
  });

  const [donationUrl, setDonationUrl] = useState('');

  const [heroTitle, setHeroTitle] = useState('');
  const [heroBackgroundImage, setHeroBackgroundImage] = useState('');
  const [uploadingHero, setUploadingHero] = useState(false);

  const [socialLinks, setSocialLinks] = useState({
    facebook: '', twitter: '', instagram: '', linkedin: '', youtube: '',
  });

  const [socialLinksVisible, setSocialLinksVisible] = useState({
    facebook: true, twitter: true, instagram: true, linkedin: true, youtube: true,
  });

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const [regionsData, settingsData] = await Promise.all([
        publicApiRequest('/api/config/regions'),
        adminApiRequest('/api/config/settings'),
      ]);
      const opts = (regionsData.regions || []).map(r => ({ value: r, label: r }));
      setRegionOptions(opts);
      setGeneral(p => ({
        ...p,
        region: settingsData.defaultRegion || (opts[0]?.value ?? ''),
      }));
      setSecurity(p => ({
        ...p,
        require2fa:      settingsData.require2fa             ?? false,
        sessionTimeout:  String(settingsData.sessionTimeoutMinutes ?? 30),
        strongPasswords: settingsData.enforceStrongPasswords ?? false,
      }));
      setNotifications(p => ({
        ...p,
        failedLoginWarnings: settingsData.failedLoginWarnings ?? true,
        weeklyDigest:        settingsData.weeklyDigest        ?? false,
      }));
      setDonationUrl(settingsData.donationUrl || '');
      setHeroTitle(settingsData.heroTitle || '');
      setHeroBackgroundImage(settingsData.heroBackgroundImage || '');
      setSocialLinks({
        facebook:  settingsData.socialLinks?.facebook  || '',
        twitter:   settingsData.socialLinks?.twitter   || '',
        instagram: settingsData.socialLinks?.instagram || '',
        linkedin:  settingsData.socialLinks?.linkedin  || '',
        youtube:   settingsData.socialLinks?.youtube   || '',
      });
      setSocialLinksVisible({
        facebook:  settingsData.socialLinksVisible?.facebook  !== false,
        twitter:   settingsData.socialLinksVisible?.twitter   !== false,
        instagram: settingsData.socialLinksVisible?.instagram !== false,
        linkedin:  settingsData.socialLinksVisible?.linkedin  !== false,
        youtube:   settingsData.socialLinksVisible?.youtube   !== false,
      });
    } catch {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const setSec  = (key) => (val) => setSecurity(p => ({ ...p, [key]: val }));
  const setGen  = (key) => (val) => setGeneral(p => ({ ...p, [key]: val }));
  const setNote = (key) => (val) => setNotifications(p => ({ ...p, [key]: val }));

  const handleExport = async () => {
    setExporting(true);
    try {
      const token    = getCookie('adminToken');
      const response = await fetch(`/api/reports/export?format=${exportFormat}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.message || 'Export failed');
      }
      const blob = await response.blob();
      const ext  = exportFormat === 'excel' ? 'xlsx' : 'csv';
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `reports-export-${new Date().toISOString().split('T')[0]}.${ext}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Export downloaded');
    } catch (err) {
      toast.error(err.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const formatPicker = (
    <div className="flex rounded-lg border border-gray-200 overflow-hidden text-xs font-bold">
      {['csv', 'excel'].map(f => (
        <button
          key={f}
          type="button"
          onClick={() => setExportFormat(f)}
          className={`px-3 py-1.5 transition-colors ${
            exportFormat === f
              ? 'bg-[#0B0D0F] text-white'
              : 'bg-white text-gray-500 hover:bg-gray-50'
          }`}
        >
          {f === 'csv' ? 'CSV' : 'Excel'}
        </button>
      ))}
    </div>
  );

  const handleHeroUpload = async (file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error('File too large. Max 2 MB.');
      return;
    }
    setUploadingHero(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const data = await adminApiRequest('/api/upload', { method: 'POST', body: formData });
      setHeroBackgroundImage(data.url);
    } catch (err) {
      toast.error(err.message || 'Upload failed');
    } finally {
      setUploadingHero(false);
    }
  };

  const handleDisableTotp = async () => {
    setDisablingTotp(true);
    try {
      const data = await adminApiRequest('/api/admin/settings/disable-totp', { method: 'DELETE' });
      updateAdmin(data.admin);
      toast.success('Authenticator app removed');
    } catch (err) {
      toast.error(err.message || 'Failed to remove authenticator app');
    } finally {
      setDisablingTotp(false);
    }
  };

  const handlePurge = async () => {
    setPurging(true);
    try {
      const data = await adminApiRequest('/api/auth/logs/purge', { method: 'DELETE' });
      toast.success(`Audit logs purged — ${data.deleted} record${data.deleted !== 1 ? 's' : ''} deleted`);
      setShowPurgeModal(false);
    } catch (err) {
      toast.error(err.message || 'Purge failed');
    } finally {
      setPurging(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminApiRequest('/api/config/settings', {
        method: 'PATCH',
        body:   JSON.stringify({
          defaultRegion:          general.region,
          sessionTimeoutMinutes:  Number(security.sessionTimeout),
          enforceStrongPasswords: security.strongPasswords,
          require2fa:             security.require2fa,
          failedLoginWarnings:    notifications.failedLoginWarnings,
          weeklyDigest:           notifications.weeklyDigest,
          donationUrl,
          heroTitle,
          heroBackgroundImage,
          socialLinks,
          socialLinksVisible,
        }),
      });
      toast.success('Settings saved successfully');
      await fetchSettings();
    } catch (err) {
      toast.error(err.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
    <AnimatePresence>
      {showTotpModal && (
        <TotpSetupModal onClose={() => setShowTotpModal(false)} />
      )}
      {showPurgeModal && (
        <PurgeLogsModal
          onClose={() => setShowPurgeModal(false)}
          onConfirm={handlePurge}
          purging={purging}
        />
      )}
    </AnimatePresence>
    <div className="space-y-5">
      <PageHeader
        title="Settings"
        subtitle="Security, notifications & platform defaults"
        action={isSuperAdmin ? (
          <button
            onClick={handleSave}
            disabled={saving || loading}
            className="flex items-center gap-2 bg-[#E52321] hover:bg-[#c91d1b] text-white px-5 py-2.5 rounded-xl text-sm font-bold transition-colors disabled:opacity-60"
          >
            {saving
              ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              : <SafeIcon icon="FiSave" className="text-base" />
            }
            Save Changes
          </button>
        ) : null}
      />

      {loading ? <SettingsSkeleton /> : isSuperAdmin ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 items-start">

          <div className="space-y-5">
            <SettingCard icon="FiShield" title="Security">
              <ToggleRow
                title="Require 2FA"
                description="Enforce two-factor auth on all staff accounts"
                checked={security.require2fa}
                onChange={setSec('require2fa')}
              />
              <ActionRow
                title="Authenticator App"
                description={admin?.totpEnabled ? 'Enabled — used at login when 2FA is required' : 'Not set up — adds a second verification method'}
                label={admin?.totpEnabled ? 'Remove' : 'Set Up'}
                danger={admin?.totpEnabled}
                onClick={admin?.totpEnabled ? handleDisableTotp : () => setShowTotpModal(true)}
                loading={disablingTotp}
              />
              <ToggleRow
                title="Enforce strong passwords"
                description="Minimum 12 chars, symbols & numbers"
                checked={security.strongPasswords}
                onChange={setSec('strongPasswords')}
              />
              <SelectRow
                title="Session timeout"
                description="Auto sign-out after inactivity"
                value={security.sessionTimeout}
                onChange={setSec('sessionTimeout')}
                options={TIMEOUT_OPTIONS}
              />
            </SettingCard>

            <SettingCard icon="FiGlobe" title="General">
              <SelectRow
                title="Default region"
                description="Applied to new reports & users"
                value={general.region}
                onChange={setGen('region')}
                options={regionOptions}
              />
              <SelectRow
                title="Timezone"
                description="Used for logs & timestamps"
                value={general.timezone}
                onChange={setGen('timezone')}
                options={TIMEZONE_OPTIONS}
              />
              <SelectRow
                title="Currency"
                description="Reporting & impact valuation"
                value={general.currency}
                onChange={setGen('currency')}
                options={CURRENCY_OPTIONS}
              />
            </SettingCard>
          </div>

          <div className="space-y-5">
            <SettingCard icon="FiBell" title="Notifications">
              <ToggleRow
                title="Email alerts"
                description="Data-integrity & security events"
                checked={notifications.emailAlerts}
                onChange={setNote('emailAlerts')}
              />
              <ToggleRow
                title="Weekly digest"
                description="Summary of published reports"
                checked={notifications.weeklyDigest}
                onChange={setNote('weeklyDigest')}
              />
              <ToggleRow
                title="Failed-login warnings"
                description="Notify on repeated failed logins"
                checked={notifications.failedLoginWarnings}
                onChange={setNote('failedLoginWarnings')}
              />
            </SettingCard>

            <SettingCard icon="FiAlertTriangle" title="Danger Zone" danger>
              <ActionRow
                title="Export all data"
                description="Download all reports as CSV or Excel"
                label="Export"
                onClick={handleExport}
                loading={exporting}
                extra={formatPicker}
              />
              <ActionRow
                title="Purge audit logs"
                description="Permanently delete logs older than 1 year"
                label="Purge"
                danger
                onClick={() => setShowPurgeModal(true)}
              />
            </SettingCard>
          </div>

          {/* Donation URL — full width */}
          <div className="sm:col-span-2">
            <SettingCard icon="FiHeart" title="Donation Link">
              <div className="pt-2">
                <label className="block text-xs font-black text-gray-500 uppercase tracking-widest mb-2">
                  Global Donation URL
                </label>
                <input
                  type="url"
                  value={donationUrl}
                  onChange={e => setDonationUrl(e.target.value)}
                  placeholder="https://donate.example.org"
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-[#E52321] focus:ring-[3px] focus:ring-[#fdecec] transition-colors placeholder-gray-300"
                />
                <p className="mt-1.5 text-xs text-gray-400">Shown as the &quot;Donate&quot; button link in the public site header. Leave blank to disable the button.</p>
              </div>
            </SettingCard>
          </div>

          {/* Hero Section — full width */}
          <div className="sm:col-span-2">
            <SettingCard icon="FiImage" title="Hero Section">
              <div className="pt-2 space-y-4">
                <div>
                  <label className="block text-xs font-black text-gray-500 uppercase tracking-widest mb-2">
                    Title
                  </label>
                  <input
                    type="text"
                    value={heroTitle}
                    onChange={e => setHeroTitle(e.target.value)}
                    placeholder="Global Projects Impact Reports"
                    className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-[#E52321] focus:ring-[3px] focus:ring-[#fdecec] transition-colors placeholder-gray-300"
                  />
                  <p className="mt-1.5 text-xs text-gray-400">Headline shown on the public homepage banner.</p>
                </div>

                <div>
                  <label className="block text-xs font-black text-gray-500 uppercase tracking-widest mb-2">
                    Background Photo
                  </label>
                  <div className="flex items-center gap-3">
                    <div className="w-16 h-16 rounded-xl bg-gray-100 border border-gray-200 flex items-center justify-center flex-shrink-0 overflow-hidden">
                      {heroBackgroundImage ? (
                        <img src={heroBackgroundImage} alt="" className="w-full h-full object-cover" onError={e => { e.target.style.display = 'none'; }} />
                      ) : (
                        <SafeIcon icon="FiImage" className="text-gray-400 text-lg" />
                      )}
                    </div>
                    <input
                      type="url"
                      value={heroBackgroundImage}
                      onChange={e => setHeroBackgroundImage(e.target.value)}
                      placeholder="Paste an image URL or upload →"
                      className="flex-1 border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-[#E52321] focus:ring-[3px] focus:ring-[#fdecec] transition-colors placeholder-gray-300"
                    />
                    <input
                      ref={heroFileInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      className="hidden"
                      onChange={e => handleHeroUpload(e.target.files[0])}
                    />
                    <button
                      type="button"
                      onClick={() => heroFileInputRef.current?.click()}
                      disabled={uploadingHero}
                      title="Upload image (max 2 MB)"
                      className="w-10 h-10 flex items-center justify-center text-gray-500 hover:text-[#E52321] border border-gray-200 rounded-xl bg-white transition-colors flex-shrink-0 disabled:opacity-50"
                    >
                      {uploadingHero
                        ? <div className="w-4 h-4 border-2 border-[#E52321] border-t-transparent rounded-full animate-spin" />
                        : <SafeIcon icon="FiUpload" className="text-sm" />
                      }
                    </button>
                  </div>
                  <p className="mt-1.5 text-xs text-gray-400">Shown behind the title on the public homepage banner. JPEG, PNG, WebP or GIF, max 2 MB.</p>
                </div>
              </div>
            </SettingCard>
          </div>

          {/* Social Media — full width below the two columns */}
          <div className="sm:col-span-2">
            <SettingCard icon="FiShare2" title="Social Media Links">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                {[
                  { key: 'facebook',  icon: 'FiFacebook',  label: 'Facebook',    placeholder: 'https://facebook.com/yourpage' },
                  { key: 'twitter',   icon: 'FiTwitter',   label: 'X (Twitter)', placeholder: 'https://x.com/yourhandle' },
                  { key: 'instagram', icon: 'FiInstagram', label: 'Instagram',   placeholder: 'https://instagram.com/yourhandle' },
                  { key: 'linkedin',  icon: 'FiLinkedin',  label: 'LinkedIn',    placeholder: 'https://linkedin.com/company/yourpage' },
                  { key: 'youtube',   icon: 'FiYoutube',   label: 'YouTube',     placeholder: 'https://youtube.com/@yourchannel' },
                ].map(({ key, icon, label, placeholder }) => {
                  const isVisible = socialLinksVisible[key] !== false;
                  return (
                    <div key={key}>
                      <div className="flex items-center justify-between mb-1.5">
                        <label className="flex items-center gap-2 text-xs font-black text-gray-500 uppercase tracking-widest">
                          <SafeIcon icon={icon} className="text-sm text-gray-400" />
                          {label}
                        </label>
                        <button
                          type="button"
                          title={isVisible ? 'Visible in footer — click to hide' : 'Hidden from footer — click to show'}
                          onClick={() => setSocialLinksVisible(p => ({ ...p, [key]: !isVisible }))}
                          className={`flex items-center gap-1 text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded-lg transition-colors ${
                            isVisible
                              ? 'text-green-600 bg-green-50 hover:bg-green-100'
                              : 'text-gray-400 bg-gray-100 hover:bg-gray-200'
                          }`}
                        >
                          <SafeIcon icon={isVisible ? 'FiEye' : 'FiEyeOff'} className="text-xs" />
                          {isVisible ? 'Visible' : 'Hidden'}
                        </button>
                      </div>
                      <input
                        type="url"
                        value={socialLinks[key]}
                        onChange={e => setSocialLinks(p => ({ ...p, [key]: e.target.value }))}
                        placeholder={placeholder}
                        disabled={!isVisible}
                        className={`w-full border rounded-xl px-4 py-2.5 text-sm focus:outline-none transition-colors placeholder-gray-300 ${
                          isVisible
                            ? 'border-gray-200 bg-gray-50 focus:border-[#E52321] focus:bg-white'
                            : 'border-gray-100 bg-gray-50/50 text-gray-400 cursor-not-allowed'
                        }`}
                      />
                    </div>
                  );
                })}
              </div>
              <p className="text-[11px] text-gray-400 mt-4 pt-3 border-t border-gray-50">
                Toggle visibility to show or hide each icon in the public footer.
              </p>
            </SettingCard>
          </div>

        </div>
      ) : (
        <div className="max-w-xl">
          <SettingCard icon="FiShield" title="Personal Security">
            <ActionRow
              title="Authenticator App"
              description={admin?.totpEnabled ? 'Enabled — used at login when 2FA is required' : 'Not set up — adds a second verification method'}
              label={admin?.totpEnabled ? 'Remove' : 'Set Up'}
              danger={admin?.totpEnabled}
              onClick={admin?.totpEnabled ? handleDisableTotp : () => setShowTotpModal(true)}
              loading={disablingTotp}
            />
          </SettingCard>
        </div>
      )}
    </div>
    </>
  );
};

export default Settings;
